import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Package, Star, Search, RotateCcw, MapPin } from "lucide-react";
import { VipBadge } from "./VipBadge";
import VenueMapConfigPanel from "./VenueMapConfigPanel";
import { T } from "../lib/i18n";

/** SuperAdmin controls — Inventory adjustment + VIP management. */
export default function AdminControls() {
  const [tab, setTab] = useState("inventory");
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button onClick={() => setTab("inventory")}
          className="px-3 py-1.5 rounded-full text-xs tracking-wide"
          style={{ background: tab === "inventory" ? "var(--motif-primary)" : "var(--motif-accent)",
                   color: tab === "inventory" ? "var(--motif-primary-fg)" : "var(--motif-text-muted)" }}
          data-testid="admin-tab-inventory">
          <Package className="w-3.5 h-3.5 inline mr-1" /> Inventory
        </button>
        <button onClick={() => setTab("vip")}
          className="px-3 py-1.5 rounded-full text-xs tracking-wide"
          style={{ background: tab === "vip" ? "var(--motif-primary)" : "var(--motif-accent)",
                   color: tab === "vip" ? "var(--motif-primary-fg)" : "var(--motif-text-muted)" }}
          data-testid="admin-tab-vip">
          <Star className="w-3.5 h-3.5 inline mr-1" /> Guest VIP
        </button>
        <button onClick={() => setTab("map")}
          className="px-3 py-1.5 rounded-full text-xs tracking-wide"
          style={{ background: tab === "map" ? "var(--motif-primary)" : "var(--motif-accent)",
                   color: tab === "map" ? "var(--motif-primary-fg)" : "var(--motif-text-muted)" }}
          data-testid="admin-tab-map">
          <MapPin className="w-3.5 h-3.5 inline mr-1" /> Venue Map
        </button>
      </div>
      {tab === "inventory" ? <InventoryPanel /> : tab === "map" ? <VenueMapConfigPanel /> : <VipPanel />}
    </div>
  );
}

function InventoryPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newRem, setNewRem] = useState("");
  const [reason, setReason] = useState("");
  const [ledger, setLedger] = useState([]);
  const [showLedger, setShowLedger] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [r, l] = await Promise.allSettled([api.get("/admin/inventory"), api.get("/admin/inventory/ledger")]);
      if (r.status === "fulfilled") setRows(r.value.data);
      if (l.status === "fulfilled") setLedger(l.value.data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (force = false) => {
    const n = parseInt(newRem, 10);
    if (!Number.isInteger(n) || `${n}` !== `${newRem}`.trim()) return toast.error("Enter a whole integer");
    if (n < 0) return toast.error("Cannot be negative");
    if (!reason.trim()) return toast.error("Reason required");
    try {
      await api.post("/admin/inventory/adjust", {
        item_type: editing.item_type, color: editing.color,
        new_remaining: n, reason: reason.trim(), force,
      });
      toast.success("Inventory adjusted");
      setEditing(null); setNewRem(""); setReason("");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.conflict === "new_remaining_below_active_reservations") {
        if (window.confirm(`${detail.message}\n\nOverride anyway?`)) return submit(true);
      } else {
        toast.error(formatErr(e));
      }
    }
  };

  const reverse = async (lid) => {
    if (!window.confirm("Reverse this adjustment via a new audited entry?")) return;
    try { await api.post(`/admin/inventory/ledger/${lid}/reverse`, {}); toast.success("Reversed"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const csvUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/inventory/reconciliation.csv`;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <a href={csvUrl} className="btn-ghost text-xs" data-testid="inv-csv">{T("Export CSV")}</a>
        <button onClick={() => setShowLedger((s) => !s)} className="btn-ghost text-xs" data-testid="inv-ledger-toggle">
          {showLedger ? "Hide" : "Show"} history ({ledger.length})
        </button>
      </div>
      {["Pashmina", "Handkerchief"].map((type) => (
        <div key={type} className="card-elev p-3">
          <div className="text-[10px] tracking-luxe text-motif-muted mb-2">{type}</div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {rows.filter((r) => r.item_type === type).map((r) => (
              <div key={r.item_type + r.color} className="flex items-center gap-2 p-1.5 rounded-lg" data-testid={`inv-row-${type.toLowerCase()}-${r.color.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="w-5 h-5 rounded border" style={{ background: r.swatch_hex || "#CCC", borderColor: "var(--motif-border)" }} />
                <div className="text-xs text-motif flex-1">{r.color}</div>
                <div className="text-[10px] text-motif-muted">start {r.starting_qty} · used {r.used_qty} · res {r.reserved_qty}</div>
                <div className="font-display text-lg text-primary-motif w-8 text-right">{r.remaining_qty}</div>
                <button onClick={() => { setEditing(r); setNewRem(String(r.remaining_qty)); }} className="btn-ghost text-[10px] px-2" data-testid={`inv-edit-${type.toLowerCase()}-${r.color.replace(/\s+/g,'-').toLowerCase()}`}>
                  Edit
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="inv-adjust-dialog">
          <div className="card-elev p-5 w-full max-w-sm space-y-2">
            <div className="font-display text-xl text-motif">Adjust {editing.item_type} {editing.color}</div>
            <div className="text-xs text-motif-muted">Current remaining: <b>{editing.remaining_qty}</b> · Active reservations: {editing.reserved_qty}</div>
            <label className="text-[10px] tracking-luxe text-motif-muted">{T("New remaining")}</label>
            <input className="input-motif" type="number" min="0" value={newRem} onChange={(e) => setNewRem(e.target.value)} data-testid="inv-new-rem" />
            <div className="text-xs text-motif-muted">Difference: <b>{(parseInt(newRem, 10) || 0) - editing.remaining_qty}</b></div>
            <label className="text-[10px] tracking-luxe text-motif-muted">{T("Reason (required)")}</label>
            <input className="input-motif" placeholder="e.g. Physical recount" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="inv-reason" />
            <div className="flex gap-2 pt-2">
              <button onClick={() => { setEditing(null); setNewRem(""); setReason(""); }} className="btn-ghost text-xs flex-1">{T("Cancel")}</button>
              <button onClick={() => submit(false)} className="btn-primary text-xs flex-1" data-testid="inv-submit">{T("Confirm")}</button>
            </div>
          </div>
        </div>
      )}

      {showLedger && (
        <div className="card-elev p-3" data-testid="inv-ledger">
          <div className="text-[10px] tracking-luxe text-motif-muted mb-2">{T("Adjustment history")}</div>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {ledger.length === 0 && <div className="text-[11px] text-motif-muted italic">{T("No adjustments yet")}</div>}
            {ledger.map((e) => (
              <div key={e.id} className="p-2 rounded-lg bg-accent-motif text-[11px] flex items-start gap-2">
                <div className="flex-1">
                  <div><b>{e.item_type} {e.color}</b>: {e.previous_qty} → {e.new_qty} ({e.delta > 0 ? "+" : ""}{e.delta}) · {e.transaction_type}</div>
                  <div className="text-motif-muted">{e.reason} — {e.actor_username} · {new Date(e.at).toLocaleString()}</div>
                </div>
                {(e.transaction_type === "MANUAL_ADJUSTMENT" || e.transaction_type === "manual_adjustment") && (
                  <button onClick={() => reverse(e.id)} className="btn-ghost text-[10px] px-1.5" data-testid={`inv-reverse-${e.id}`} title="Reverse">
                    <RotateCcw className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VipPanel() {
  const [guests, setGuests] = useState([]);
  const [q, setQ] = useState("");
  const [filterVip, setFilterVip] = useState("all");
  const [selected, setSelected] = useState(new Set());

  const refresh = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (filterVip === "vip") params.set("vip", "true");
      if (filterVip === "non") params.set("vip", "false");
      const { data } = await api.get(`/admin/guests${params.toString() ? "?" + params.toString() : ""}`);
      setGuests(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, [q, filterVip]);
  useEffect(() => { refresh(); }, [refresh]);

  const toggleVip = async (gid, vip) => {
    try { await api.put(`/admin/guests/${gid}/vip`, { vip }); toast.success(vip ? "Marked VIP" : "VIP removed"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const bulk = async (vip) => {
    const ids = [...selected];
    if (ids.length === 0) return toast.error("Select guests first");
    try { const { data } = await api.post("/admin/guests/bulk-vip", { guest_ids: ids, vip }); toast.success(`${data.count} updated`); setSelected(new Set()); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const stats = useMemo(() => ({
    invited_vip: guests.filter((g) => g.vip && !g.is_couple).length,
    couple_vip: guests.filter((g) => g.vip && g.is_couple).length,
  }), [guests]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-motif-muted" />
          <input className="input-motif pl-9" placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vip-search" />
        </div>
        <select className="input-motif w-32" value={filterVip} onChange={(e) => setFilterVip(e.target.value)} data-testid="vip-filter">
          <option value="all">All</option>
          <option value="vip">{T("VIP only")}</option>
          <option value="non">{T("Non-VIP")}</option>
        </select>
      </div>
      <div className="flex gap-2 text-[11px] text-motif-muted">
        <div>Invited VIPs: <b className="text-primary-motif">{stats.invited_vip}</b></div>
        <div>Couple VIPs: <b className="text-primary-motif">{stats.couple_vip}</b></div>
        {selected.size > 0 && (
          <div className="ml-auto flex gap-1">
            <button onClick={() => bulk(true)} className="btn-primary text-[10px] px-2 py-1" data-testid="vip-bulk-add">Mark VIP ({selected.size})</button>
            <button onClick={() => bulk(false)} className="btn-ghost text-[10px] px-2 py-1" data-testid="vip-bulk-remove">Remove VIP ({selected.size})</button>
          </div>
        )}
      </div>
      <div className="card-elev p-2 max-h-96 overflow-y-auto">
        {guests.map((g) => (
          <div key={g.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-accent-motif" data-testid={`vip-row-${g.id}`}>
            <input
              type="checkbox"
              checked={selected.has(g.id)}
              onChange={(e) => { const s = new Set(selected); e.target.checked ? s.add(g.id) : s.delete(g.id); setSelected(s); }}
              data-testid={`vip-check-${g.id}`}
            />
            <VipBadge show={!!g.vip} />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-motif truncate">{g.full_name}</div>
              <div className="text-[10px] text-motif-muted">
                {g.is_couple ? "Couple" : g.table_name || "—"}{g.seat ? ` · Seat ${g.seat}` : ""}
              </div>
            </div>
            <button
              onClick={() => toggleVip(g.id, !g.vip)}
              className={g.vip ? "btn-ghost text-[10px] px-2 py-1" : "btn-primary text-[10px] px-2 py-1"}
              data-testid={`vip-toggle-${g.id}`}
            >
              {g.vip ? "Remove VIP" : "Mark VIP"}
            </button>
          </div>
        ))}
        {guests.length === 0 && <div className="text-center text-motif-muted text-xs py-4">{T("No guests match")}</div>}
      </div>
    </div>
  );
}
